/* eslint-disable react-hooks/rules-of-hooks */
import { Button, DialogLazy, MainLoader, Modal, ModalContent, ModalFooter, ModalHeader, Notification, removeMainLoader, SlidingMenu } from "@netapp/bxp-design-system-react";
import classNames from "classnames";
import { FederationTestPage, RedirectToFederatedConnectionTest } from "components/FerderateConnectionTestPage";
import _ from "lodash";
import AppNotifications from "modules/app/components/AppNotifications";
import AppRichNotifications from "modules/app/components/AppRichNotifications";
import ClaimOrgAdminDialog from "modules/app/components/claimOrgAdmin/ClaimOrgAdminDialog";
import { MonitorCurrentConnector } from "modules/current-connector";
import React, { Suspense, useEffect, useRef, useState, useCallback, memo } from 'react';
import { lazy } from "react-imported-component/macro";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router";
import { Redirect, Route, Switch } from 'react-router-dom';
import { history } from "store";
import { buildAddAgentLocation } from "utils/addAgentUtil";
import { getItemAnon, isNewTenancyEnabled, setItemAnon } from "utils/localstorage";
import { setDocumentTitleAndMeta } from "utils/documentUtils";
import styles from "./app.module.scss";
import DefaultRedirectRoute from './modules/app-redirect/components/Endpoint';
import NgWrapper from "./modules/app/NgWrapper";
import ServiceIntegration from "./modules/app/ServiceIntegration";
import AppBootPerformanceLogger from "./modules/app/components/AppBootPerformanceLogger";
import DefaultRoute from "./modules/app/components/DefaultRoute";
import GTCDataCollectionPage from "./modules/app/components/GTCDataCollectionPage";
import InactiveConnectorDialog from "./modules/app/components/InactiveConnectorDialog";
import MainErrorBoundary from "./modules/app/components/MainErrorBoundary";
import MainHeader from "./modules/app/components/MainHeader";
import NclSmtpSetup from "./modules/app/components/NclSmtpSetup";
import MainNav from "./modules/app/components/MainNav";
import { Forbidden } from "./modules/app/components/NoAccessComponents";
import NoOrgDialog from "./modules/app/components/NoOrgDialog";
import SlidingManagementWidget from './modules/app/components/SlidingManagementWidget';
import MoveToSaas from './modules/app/components/moveToSaas/MoveToSaas';
import WhatsNew from './modules/app/components/whatsNew/WhatsNew';
import { whatsNewConfig } from './modules/app/components/whatsNew/whatsConfig';
import CategoriesNav from "modules/app/components/headerMenus/leftNav/CategoriesNav";
import SelectedServiceNav from "modules/app/components/headerMenus/leftNav/components/SelectedServiceNav";
import useCurrentService from "utils/useCurrentService";
import { useTranslation } from "react-i18next";
import { appComponents as i18nComponentsNamespace, vendor as i18nVendorNamespace} from "./i18n/namespace";
import useServicesAccessHistory from "utils/useServicesAccessHistory";
import {PREFERENCES_KEYS} from "./modules/app/middlewares/userPreferencesMiddleware";
import { useFeatureFlag } from "./utils/useFeatureFlag";
import { isNcl } from "utils/app/utils";
import { initializeClientInterceptors } from "utils/clientsInterceptor";
import { useMarketplaceAppEffects } from "./modules/app/hooks/useMarketplaceAppEffects";

const HomePage = lazy(() => import('./modules/home-page' /* webpackChunkName: "HomePage" */).then(r => r.HomePage));

const NclHomePage = lazy(() => import('./modules/ncl-home-page' /* webpackChunkName: "NclHomePage" */).then(r => r.HomePage));

const GalleryTab = lazy(() => import('./modules/gallery'/* webpackChunkName: "Gallery" */).then(r => r.GalleryTab));

const SplashScreen = lazy(() => import('./modules/app/components/splashScreen/SplashScreen' /* webpackChunkName: "SplashScreen" */));

const AiAssistantPanel = lazy(() => import('./modules/app/components/headerMenus/AiAssistant/components/AiAssistantPanel' /* webpackChunkName: "AiAssistantPanel" */));

export const ProxyDialog = process.env.REACT_APP_IS_DARK !== "true" ?
    lazy(() => import("./modules/local" /* webpackChunkName: "Local" */).then(r => r.ProxyDialog)) :
    lazy(() => import("./modules/dark" /* webpackChunkName: "Dark" */).then(r => r.ProxyDialog));

export const SetupDialog = (() => {
    if (isNcl()) {
        return lazy(() => import("./modules/ncl-onboarding" /* webpackChunkName: "Local" */).then(r => r.SetupDialog));
    }
    if (process.env.REACT_APP_IS_DARK !== "true") {
        return lazy(() => import("./modules/local" /* webpackChunkName: "Local" */).then(r => r.SetupDialog));
    }
    return lazy(() => import("./modules/dark" /* webpackChunkName: "Dark" */).then(r => r.SetupDialog));
})();


export const Login = (() => {
    if (isNcl()) {
        return lazy(() => import("./modules/ncl-onboarding" /* webpackChunkName: "NCL" */).then(r => r.Login))
    }
    return lazy(() => import("./modules/dark" /* webpackChunkName: "Dark" */).then(r => r.Login));
})();

const CVS = lazy(() => import('./modules/cvs' /* webpackChunkName: "cvs"*/));

const NetappInternal = lazy(() => import('./modules/netapp-internal' /* webpackChunkName: "Internal"*/));

const Support = lazy(() => import('./modules/support' /* webpackChunkName: "Support"*/));

const Licenses = lazy(() => import('./modules/licenses' /* webpackChunkName: "Licenses"*/));

const StorageResources = lazy(() => import('./modules/storage' /* webpackChunkName: "StorageResources"*/));

const HttpsSetup = lazy(() => import('modules/connector-settings/index' /* webpackChunkName: "HTTPS"*/).then(r => r.HttpsSetup));

// Lazy load Identity and Access Management module.
const IdentityAndAccessMgmt = lazy(() => import('modules/identity-access-management/index' /* webpackChunkName: "Tenancy"*/).then(r => r.Settings));

const AgentsManagement = lazy(() => import('modules/agents-mgmt' /* webpackChunkName: "AgentsManagement" */));

const Timeline = lazy(() => import('./modules/timeline' /* webpackChunkName: "Timeline"*/));

const AddNewWorkingEnvironment = lazy(() => import('./modules/add-new-working-environment' /* webpackChunkName: "AddNewWorkingEnvironment"*/));

const ReplicationTab = lazy(() => import('./modules/replication' /* webpackChunkName: "Replication"*/).then(r => r.ReplicationTab));

const ReplicationSetupWizard = lazy(() => import('./modules/replication/wizard' /* webpackChunkName: "ReplicationWizard"*/).then(r => r.ReplicationSetupWizard));

const Settings = lazy(() => import('modules/connector-settings/index' /* webpackChunkName: "Settings"*/).then(r => r.Settings));

const ValidateAgent = lazy(() => import('modules/connector-settings/index' /* webpackChunkName: "ValidateAgentDashboard"*/).then(r => r.ValidateAgentDashboard));

const Credentials = lazy(() => import('modules/credentials' /* webpackChunkName: "Credentials"*/));

const AlertsAndNotifications = lazy(() => import('modules/alerts-and-notifications' /* webpackChunkName: "AlertsAndNotifications"*/))

const RedirectManager = lazy(() => import('modules/app-redirect' /* webpackChunkName: "AppRedirect"*/));

const Alerts = lazy(() => import('./modules/bxp-alerts' /* webpackChunkName: "bxp-alerts"*/));

const Console = lazy(() => import('./modules/console' /* webpackChunkName: "console"*/));

const Labels = lazy(() => import('./modules/labels' /* webpackChunkName: "labels"*/));

const WorkloadAnalyzer = lazy(() => import('./modules/workload-analyzer' /* webpackChunkName: "workload-analyzer"*/).then(r => r.WorkloadAnalyzer));

const AIQUM = lazy(() => import('./modules/aiqum-migration' /* webpackChunkName: "aiqum"*/).then(r => r.AiqumMigration));

const AddAgent = lazy(() => import('modules/add-agent' /* webpackChunkName: "AddAgent" */));
const QuickModeAddAgent = lazy(() => import('modules/quick-mode-add-agent' /* webpackChunkName: "QuickModeAddAgent" */));

const OnpremResources = lazy(() => import('modules/onprem' /* webpackChunkName: "Onprem"*/).then(r => r.OnpremResources));

const FleetManagement = lazy(() => import('./modules/fleet-management' /* webpackChunkName: "FleetManagement"*/).then(r => r.FleetManagement));

const FleetPoliciesOverview = lazy(() => import('./modules/storage-class/FleetPoliciesOverview' /* webpackChunkName: "FleetPoliciesOverview"*/).then(r => r.default));

const ClassesRoutes = lazy(() => import('./modules/storage-class/classes/ClassesRoutes' /* webpackChunkName: "ClassesRoutes"*/).then(r => r.default));

const PoliciesRoutes = lazy(() => import('./modules/storage-class/policies/PoliciesRoutes' /* webpackChunkName: "PoliciesRoutes"*/).then(r => r.default));

const ConsoleAlerts = lazy(() => import('./modules/console-alerts' /* webpackChunkName: "console-alerts"*/));

const AITools = lazy(() => import('modules/ai-tools' /* webpackChunkName: "ai-tools" */).then(r => r.AITools));

const CustomDashboard = lazy(() => import('./modules/custom-dashboard' /* webpackChunkName: "CustomDashboard"*/).then(r => r.CustomDashboard));

const AddDashboard = lazy(() => import('./modules/custom-dashboard' /* webpackChunkName: "AddDashboard"*/).then(r => r.AddDashboard));

export const UserMFADetails = lazy(() => import('./modules/mfa/index').then(r => r.UserMFADetails));

const OnpremDiscovery = lazy(() => import('./modules/gallery/components/working-environments/OnpremDiscovery' /* webpackChunkName: "OnpremDiscovery"*/));

const DirectoryServices = lazy(() => import('modules/identity-access-management/components/directoryServices/index' /* webpackChunkName: "DirectoryServices" */));

const routesAvailableWithoutAnActiveConnector = ["/working-environments", "/timeline", "/replication", "/sync", "/add-working-environment-wizard", "/cloud-volumes-service", "/credentials"];

const SelectedServiceNavWrapper = memo(() => {
    const isShowingCategoriesNav = useSelector(state => state.menu.activeWidget === 'leftNav');
    return <SelectedServiceNav isShowingCategoriesNav={isShowingCategoriesNav}/>;
});


const SlidingMenuWrapper = memo(({ slidingMenuRef, mainHeaderRef, onCategoryMenuItemClicked }) => {
    const dispatch = useDispatch();
    const isShowingCategoriesNav = useSelector(state => state.menu.activeWidget === 'leftNav');
    
    // Somehow stopping the propagation at bubbling phase doesn't work, so we use capture phase.
    // Add global event interceptor to prevent SlidingMenu from closing when clicking in flyout menu.
    useEffect(() => {
        if (!isShowingCategoriesNav) {
            return;
        }

        const handleFlyoutMenuClick = (e) => {
            const isInFlyoutMenu = e.target.closest('[data-flyout-menu]');
            
            if (isInFlyoutMenu) {
                // Check if the click is on an interactive element (buttons, links, service cards)
                const isOnInteractiveElement = e.target.closest('[data-service-card], [data-favorite-button]');
                
                if (!isOnInteractiveElement) {
                    // Only prevent if clicking on empty space, not on interactive elements
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    return false;
                }
            }
        };

        // Use capture phase to intercept before SlidingMenu's event handlers
        document.addEventListener('click', handleFlyoutMenuClick, true);
        document.addEventListener('mousedown', handleFlyoutMenuClick, true);
        
        return () => {
            document.removeEventListener('click', handleFlyoutMenuClick, true);
            document.removeEventListener('mousedown', handleFlyoutMenuClick, true);
        };
    }, [isShowingCategoriesNav]);

    const handleCloseTrigger = useCallback(() => {
        dispatch({
            type: "MENU:CLOSE-WIDGET"
        });
    }, [dispatch]);
    
    return (
        <SlidingMenu 
            isActive={isShowingCategoriesNav} 
            isSlidingFromLeft={true} 
            top={72} 
            closeTrigger={handleCloseTrigger} 
            className={styles['sliding-menu-left-nav']}
        >
            <CategoriesNav ref={slidingMenuRef} mainHeaderRef={mainHeaderRef} onCategoryMenuItemClicked={onCategoryMenuItemClicked} isShowingCategoriesNav={isShowingCategoriesNav} />
        </SlidingMenu>
    );
});

const TimeoutModal = () => {
    const { t } = useTranslation(i18nComponentsNamespace);
    const dispatch = useDispatch();
    const renew = () => {
        dispatch({
            type: "AUTH:RENEW-SSO"
        })
    };

    return <Modal closeTrigger={renew}>
        <ModalHeader>
            {t("authentication_renew")}
        </ModalHeader>
        <ModalContent>
            {t("session_expired")}
        </ModalContent>
        <ModalFooter>
            <Button onClick={renew}>{t("refresh")}</Button>
        </ModalFooter>
    </Modal>;
};

const MainApp = () => {
    const [launchClaimDialog, setLaunchClaimDialog] = useState(false);

    const serviceNavigationKeysRef = useRef({});
    const mainHeaderRef = useRef(null);
    const skipTargetRef = useRef(null);  
    const mainNavRef = useRef(null);     
    const slidingMenuRef = useRef(null); 

    const { t } = useTranslation(i18nComponentsNamespace);
    
    const dispatch = useDispatch();
    const location = useLocation();
    const serviceRoot = `/${location.pathname.split("/")[1] || ""}`;
    if (location.state?.navigationId) {
        serviceNavigationKeysRef.current[serviceRoot] = location.state.navigationId;
    }
    const serviceIntegrationKey = serviceNavigationKeysRef.current[serviceRoot];

    const isLoadingServices = useSelector(state => state.app.isLoadingServices);
    const serviceIntegrationPaths = useSelector(state => state.app.serviceIntegrationPaths);
    const { isAuthenticated, userId, renewTimeout, status } = useSelector(state => state.auth);
    const showGTCDataCollectionModal = useSelector(state => state.app.showGTCDataCollectionModal);
    const { accountStatus, selectedAccountId, workspaceStatus, agentStatus, selectedAgentId, forbidden, isChangedAgent, isTransitionFromUnselectedToSelected, permissions, selectedAccount, orgStatus, scopeStatus, selectedOrgId, orgs, hasOrgAdmin, hasFetchOrgAdminFailed, isSmtpConfigured } = useSelector(state => state.tenancy);
    const isFeaturesInitialized = useSelector(state => state.features.isInitialized);
    const isClaimOrgFeatureFlagEnabled = useSelector((state) => state.features.active['NetApp.TenancyV4/claimOrg'] ?? false);
    const isBrowserCachingEnabled = useFeatureFlag("Platform.BlueXP/BrowserCaching");
    const currentService = useCurrentService();
    const features = useSelector(state => state.features);
    const { isBrownFieldUser } = useSelector(state => state.app);
    const isStargateDisabled = useSelector((state) => state.features.active['NetApp.DisableStargate/*'] ?? false);

    const whatsNewHandler = useCallback(() => {
        setItemAnon({ itemKey: `dontShowWhatsNew` }, true);
    }, []);

    const isTenancyV4 = isNewTenancyEnabled();
    const fromAppRedirect = !!location?.state?.isAppRedirect;
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams ? searchParams.get("email") : undefined;
    const isActiveIq = (matchPath(location.pathname, { path: '/active-iq' }))?.isExact ?? false;
    const isSaaSUI = process.env.REACT_APP_IS_SAAS === 'true';
    const isHomePageEnabled = useSelector((state) => (state.features.active['NetApp.Home/*'] && isSaaSUI) ?? false);
     const orgTags = useSelector(state => !_.isEmpty(state.tenancy?.selectedOrg) ? state.tenancy?.selectedOrg?.tags : undefined);    
    const isNssUser = userId?.toLowerCase().includes('|bluexp-federation|');
    const selectedScope = useSelector(state => state.tenancy?.selectedScope);
    const fleetId = selectedScope?.id;

    const { data: isSmtpSkipped = false } = useSelector(
        state => state.userPreferences[PREFERENCES_KEYS.SMTP_SETUP_SKIPPED_PREFERENCE_KEY] || {}
    );
    
    useEffect(() => {
        if (isBrowserCachingEnabled) {
            setItemAnon({ itemKey: "feature.Platform.BlueXP/BrowserCaching" }, true);
        } else {
            setItemAnon({ itemKey: "feature.Platform.BlueXP/BrowserCaching" }, false);
        }
    }, [isBrowserCachingEnabled]);

    useEffect(() => {
        if (isAuthenticated && !_.isEmpty(features?.active)) {
            if (isTenancyV4) {
                dispatch({
                    type: (isSaaSUI && !isStargateDisabled) ? "TENANCYV4:FETCH-USER-DETAILS" : "TENANCYV4:FETCH-ORGS",
                    payload: { userId, pollAgents: true }
                });
            }
        }
    }, [isAuthenticated, userId, dispatch, isStargateDisabled, features, isSaaSUI, isTenancyV4]);

    useEffect(() => {
        if (!isLoadingServices && isAuthenticated) { //checks isAuthenticated cause feature flag manual toggles get saved with the userId
            dispatch({
                type: "APP:INITIALIZE-FEATURES"
            })
        }
        
    }, [isLoadingServices, dispatch, isAuthenticated, accountStatus, orgStatus]);

    useEffect(() => {
        if (isFeaturesInitialized && (accountStatus === "SUCCESS" || orgStatus === "SUCCESS")) { //checks accountStatus cause menu favourites get saved with accountId
            dispatch({
                type: "MENU:INITIALIZE"
            })
        }
    }, [isFeaturesInitialized, accountStatus, orgStatus, dispatch]);

    useEffect(() => {
        if (isActiveIq && !_.isEmpty(emailParam)) {
            whatsNewHandler()
        }
    }, [emailParam, isActiveIq, whatsNewHandler]);

    useEffect(() => {
        setLaunchClaimDialog(!_.isNil(hasOrgAdmin) && !hasOrgAdmin && !hasFetchOrgAdminFailed);
    }, [hasOrgAdmin, hasFetchOrgAdminFailed]);

    useEffect(() => {
        if (isClaimOrgFeatureFlagEnabled && isSaaSUI && !_.isNil(selectedOrgId)) {
            dispatch({
                type: "TENANCYV4:FETCH-ORG-ADMINS",
                payload: {
                    selectedOrgId
                }
            });
        }
        if (isHomePageEnabled && !_.isNil(selectedOrgId)) {
            dispatch({
                type: "APP:FETCH-BROWNFIELD-DETAILS",
                payload: {
                    selectedOrgId
                }
            });
        }
        if(isNcl() && !_.isNil(selectedOrgId)) {
            dispatch({
                type: "APP:FETCH-BROWNFIELD-DETAILS",
                payload: {
                    selectedOrgId
                }
            });
        }
    }, [selectedOrgId, dispatch, isClaimOrgFeatureFlagEnabled, isSaaSUI, isHomePageEnabled, isAuthenticated]);

    // Fetch feature manager features by organization when org is selected and user is authenticated
    useEffect(() => {
        if (selectedOrgId && isAuthenticated && isSaaSUI) {
            dispatch({
                type: "FEATURE-MANAGER:FETCH-FEATURES-BY-ORG",
                payload: {
                    organizationId: selectedOrgId
                }
            });
        }
    }, [selectedOrgId, isAuthenticated]);

    useEffect(() => {
        if(isAuthenticated && isSaaSUI) {
            dispatch({
                type: "PREFERENCES:FETCH",
                payload: { name: PREFERENCES_KEYS.APP_PREFERENCES }
            });
        }
        if(isAuthenticated && isNcl()) {
            dispatch({
                type: "PREFERENCES:FETCH",
                payload: { name: PREFERENCES_KEYS.SMTP_SETUP_SKIPPED_PREFERENCE_KEY }
            });
        }

    }, [dispatch, isAuthenticated, isSaaSUI]);

     useEffect(() => {
        if ((isSaaSUI && isNssUser && !_.isEmpty(orgTags))) {  
                      
                //Delaying the dispatch, so it won't effect the performace of our APP loading. 
                const timeoutId = setTimeout(() => {                   
                    dispatch({
                        type: "TENANCYV4:FETCH-USER-CMATIDS"
                    });
                }, 0);

                return () => clearTimeout(timeoutId);            
        }
    }, [orgTags, isNssUser, isSaaSUI, dispatch]);
    useEffect(() => {
        if (isAuthenticated) {
            initializeClientInterceptors();
        }
    }, [isAuthenticated]);

    useMarketplaceAppEffects({ isAuthenticated, isSaaSUI, selectedOrgId });

    useServicesAccessHistory(); //tracking usage of services in the app
    const dontShowMoveToSaasScreen = window.localStorage.getItem('occm.dont.show.move.to.saas.overlay') === "true";
    const dontShowMovedNavDialog = getItemAnon({ itemKey: `dontShowNewBlue` });
    const dontShowWhatsNew = getItemAnon({ itemKey: `dontShowWhatsNew` });
    const noActiveAgentInSaasMode = process.env.REACT_APP_IS_SAAS === "true" && agentStatus === "SUCCESS" && !selectedAgentId;
    const agentButtonKey = "agent";

    function onCategoryMenuItemClicked() {
        dispatch({
            type: "MENU:CLOSE-WIDGET"
        });
    }

    if (isLoadingServices || !isAuthenticated) {
        if (status === 'PENDING') {
            return <MainLoader/>;
        } else if (status === 'UNSET') {
            return ""
        }
    }
    const nclSmtpNotConfigured = isNcl() && isSmtpConfigured === false;
    const showingSmtpSetup = nclSmtpNotConfigured && !isSmtpSkipped;
    const showTabs = agentStatus !== "PENDING" && (selectedAccountId || selectedOrgId) && (isTenancyV4 ? !_.isEmpty(permissions) && orgStatus === "SUCCESS" && scopeStatus === "SUCCESS" : accountStatus === "SUCCESS"  && workspaceStatus === "SUCCESS") && !showingSmtpSetup;
    const noOrganizationForUser = orgStatus === "SUCCESS" && !selectedOrgId && (orgs?.length === 0) && !forbidden;
    const showLoader = !showingSmtpSetup && ((isTenancyV4 && (_.isEmpty(permissions) && !forbidden) && !noOrganizationForUser && !showingSmtpSetup) || accountStatus === "PENDING" || (workspaceStatus === "PENDING" && selectedAccountId) || (agentStatus === "PENDING" && selectedAccountId) || orgStatus === "PENDING" || (scopeStatus === "PENDING" && selectedOrgId) || (agentStatus === "PENDING" && selectedOrgId));
    const showLeftNav = ((accountStatus === "SUCCESS" && !isTenancyV4) || (isTenancyV4 && orgStatus === "SUCCESS" && !noOrganizationForUser)) && !showingSmtpSetup;
    const noAccountForUser = accountStatus === "SUCCESS" && !selectedAccountId && !forbidden;

    return <div className={classNames((currentService?.isShowingSelectedServiceNav) ? styles.appBase : styles.noNavBase, { [styles['initializing']]: !showLeftNav })}>
        {process.env.REACT_APP_ENDPOINTS !== "prod" && <AppBootPerformanceLogger/>}
        <MainHeader ref={mainHeaderRef} skipTargetRef={skipTargetRef} navRef={slidingMenuRef} noOrganizationForUser ={noOrganizationForUser}/>
        {showLeftNav && <>
            {currentService?.isShowingSelectedServiceNav && <SelectedServiceNavWrapper/>}
        </>}
        <SlidingMenuWrapper slidingMenuRef={slidingMenuRef} mainHeaderRef={mainHeaderRef} onCategoryMenuItemClicked={onCategoryMenuItemClicked}/>
        {isNcl() && <Suspense fallback={null}><AiAssistantPanel /></Suspense>}
        
        {showGTCDataCollectionModal && <GTCDataCollectionPage />}
         <div className={((noOrganizationForUser || showingSmtpSetup) && isTenancyV4) ? '': styles.doodads}>
            {noOrganizationForUser && isTenancyV4 && !showGTCDataCollectionModal && (
                <NoOrgDialog />
            )} 
            {!noOrganizationForUser && showingSmtpSetup && (
                <NclSmtpSetup />
            )}

            {isBrownFieldUser && isSaaSUI && <SplashScreen />}           
            {isSaaSUI && isClaimOrgFeatureFlagEnabled && launchClaimDialog && !showGTCDataCollectionModal && !renewTimeout && <ClaimOrgAdminDialog closeTrigger={() => setLaunchClaimDialog(false)} />}
            {isChangedAgent && !launchClaimDialog && <InactiveConnectorDialog/>}
            {process.env.REACT_APP_IS_SAAS !== "true" && !dontShowMoveToSaasScreen && process.env.REACT_APP_IS_DARK !== 'true' && selectedAccount?.isSaas && <MoveToSaas/>}
            { /**process.env.REACT_APP_IS_SAAS === "true" && !dontShowMovedNavDialog && selectedAccountId && <NavMovedDialog/> */}
            {process.env.REACT_APP_IS_DARK === "true" && !isNcl() && !noOrganizationForUser && isTenancyV4 && dontShowMovedNavDialog && !dontShowWhatsNew && !showLoader && <WhatsNew whatsNewConfig={whatsNewConfig} handleCheckboxChange={whatsNewHandler}/>}
            {renewTimeout && <TimeoutModal/>}
            <SlidingManagementWidget/>
            <MonitorCurrentConnector/>
            {isTransitionFromUnselectedToSelected && <Notification type="info" style={{ width: 820, position: "fixed", bottom: 63, left: "50%", transform: "translateX(-50%)" }}>
               {t("active_connector")}
            </Notification>}            
        </div>
        <button 
            ref={skipTargetRef} 
            tabIndex={-1} 
            aria-label={t("press_tab_key")}
            className={styles['skip-target']}
        ></button>
        <div className={styles.container}>
            {forbidden && <Forbidden/>}
            <MainErrorBoundary>
                <Suspense fallback={<MainLoader/>}>
                    <Switch>
                        <Route path="/netapp-internal" render={() => <NetappInternal/>}/>
                        <Route path="/federated-connection-test" render={() => <FederationTestPage/>}/>
                        <Route path="/redirect-to-federated-connection-test" render={() => <RedirectToFederatedConnectionTest/>}/>
                        <Route>
                            <>
                                {showLoader && <MainLoader/>}
                                {showTabs && <Switch>
                                    <Route path="/app-redirect/:app" render={() => <DefaultRoute><RedirectManager/></DefaultRoute>}/>
                                    <Route path="/storage/on-prem/local" render={() => <DefaultRoute><StorageResources isONTAPDirect="true"/></DefaultRoute>} />
                                    <Route path="/storage/on-prem/connector" render={() => <DefaultRoute><StorageResources isONTAPDirect="false"/></DefaultRoute>} />
                                    <Route path="/storage/on-prem" render={() => <DefaultRoute><OnpremResources /></DefaultRoute>} /> 
                                    <Route path={serviceIntegrationPaths} render={() => <ServiceIntegration key={serviceIntegrationKey}/>} />
                                    <Route path="/support-dashboard" render={() => <Support />} />
                                    <Route path="/add-working-environment" render={() => <AddNewWorkingEnvironment />} />
                                    {isNcl() && <Route path="/directory-services" render={() => <DefaultRoute><DirectoryServices /></DefaultRoute>} />}
                                    {!isNcl() && <Route path="/add-working-environment-wizard" render={() => <Redirect to={{ pathname: "/add-working-environment/choose-type", hash: window.location.hash, state: history.location.state }} />} />}
                                    {isNcl() && <Route path="/add-working-environment-wizard" render={() => <Redirect to={{ pathname: `/fleets/${fleetId}/add-systems`, hash: window.location.hash, state: { ...(history.location.state || {}), fleet: selectedScope } }} />} />}
                                    <Route path="/agents-mgmt/:agentId/https-setup" render={() => <HttpsSetup />} />
                                    <Route path="/agents-mgmt/:agentId/cvo-settings" render={() => <Settings />} />
                                    <Route path="/agents-mgmt/:agentId/validate" render={() => <ValidateAgent />} />
                                    <Route path="/agents-mgmt/:agentId/edit" render={() => <DefaultRoute><AgentsManagement /></DefaultRoute>} />
                                    <Route path="/agents-mgmt/:agentId/remove" render={() => <DefaultRoute><AgentsManagement /></DefaultRoute>} />
                                    <Route path="/identity-access-mgmt/" render={() => <DefaultRoute><IdentityAndAccessMgmt /></DefaultRoute>} />
                                    <Route path="/agents-mgmt/quick-add-agent/:provider" render={() => <DefaultRoute><QuickModeAddAgent /></DefaultRoute>} />
                                    <Route
                                        path="/agents-mgmt/add-agent"
                                        render={({ location }) => {
                                            const provider =
                                                location.state?.selectedProvider?.toLowerCase?.()
                                                || location.hash?.replace(/^#/, "").toLowerCase();
                                            return provider === "aws"
                                                ? <Redirect to={buildAddAgentLocation(provider, location.state)} />
                                                : <DefaultRoute><AddAgent /></DefaultRoute>;
                                        }}
                                    />
                                    <Route path="/agents-mgmt" render={() => <DefaultRoute><AgentsManagement /></DefaultRoute>} />
                                    <Route path="/credentials" render={() => <DefaultRoute><Credentials /></DefaultRoute>} />
                                    <Route path="/ai-settings" render={() => <DefaultRoute><AITools /></DefaultRoute>} />
                                    {isNcl() && <Route path="/fleet-management" render={({location}) => <Redirect to={{ ...location, pathname: location.pathname.includes('/fleets/') ? location.pathname.replace('/fleet-management', '') : location.pathname.replace('/fleet-management', '/fleets') }} />} />}
                                    {isNcl() && <Route path="/fleets" render={() => <DefaultRoute><FleetManagement /></DefaultRoute>} />}
                                    {isNcl() && <Route path="/storage-classes" render={() => <DefaultRoute><FleetPoliciesOverview /></DefaultRoute>} />}
                                    <Route path="/storage/:serviceKey/:provider?" render={() => <DefaultRoute><StorageResources /></DefaultRoute>} />
                                    <Route path="/cvs/:flavour/:weId?" render={() => <CVS />} />
                                    <Route path="/timeline" render={() => <DefaultRoute><Timeline /></DefaultRoute>} />
                                    <Route path="/working-environments"><DefaultRoute><GalleryTab /></DefaultRoute></Route>
                                    <Route path="/replication/setup"><DefaultRoute><ReplicationSetupWizard /></DefaultRoute></Route>
                                    <Route path="/replication"><DefaultRoute><ReplicationTab /></DefaultRoute></Route>
                                    <Route path="/licenses" render={({location}) => <Redirect to={{ ...location, pathname: location.pathname.replace('/licenses', '/licenses-and-subscriptions') }} />} />
                                    <Route path="/digital-wallet" render={({location}) => <Redirect to={{ ...location, pathname: location.pathname.replace('/digital-wallet', '/licenses-and-subscriptions') }} />} />
                                    <Route path="/licenses-and-subscriptions" render={() => <DefaultRoute><Licenses /></DefaultRoute>} />
                                    <Route path="/alerts-and-notifications" render={() => <DefaultRoute><AlertsAndNotifications/></DefaultRoute>}/>
                                    <Route path="/alerts" render={() => <DefaultRoute>{isNcl() ? <ConsoleAlerts /> : <Alerts />}</DefaultRoute>} />
                                    {isNcl() && <Route path="/console-configurations" render={() => <DefaultRoute><Console /></DefaultRoute>} />}
                                    {isNcl() && <Route path="/labels" render={() => <DefaultRoute><Labels /></DefaultRoute>} />}
                                    {isNcl() && <Route path="/workload-analyzer" render={() => <DefaultRoute><WorkloadAnalyzer /></DefaultRoute>} />}
                                    {isNcl() && <Route path="/aiqum-migration" render={() => <DefaultRoute><AIQUM /></DefaultRoute>} />}
                                    {isNcl() && <Route path="/custom-dashboard" render={() => <DefaultRoute><CustomDashboard/></DefaultRoute>} />}
                                    {isNcl() && <Route path="/custom-dashboard/add-dashboard" render={() => <DefaultRoute><AddDashboard/></DefaultRoute>} />}
                                    <Route path="/home" render={() => <DefaultRoute>{isNcl() ? <NclHomePage /> : <HomePage />}</DefaultRoute>}/>                                                              
                                    <Route path={routesAvailableWithoutAnActiveConnector} render={() => <DefaultRoute/>}/>
                                    {noActiveAgentInSaasMode && permissions.addConnector && <Route path="/:path" render={() => <Redirect to={{ ...history.location, hash: "#addConnector" }}/>}/>}
                                    {fromAppRedirect && <Route path="/:path" render={() => <DefaultRedirectRoute/>}/>} {/* if it redirected to junk urls, then enable this route and show something went wrong during redirection error. */}
                                    <Route path="/:path" render={() => <DefaultRoute/>}/>
                                    <Redirect to={{ pathname: "/home" , hash: window.location.hash }}/>
                                </Switch>}
                            </>
                        </Route>
                    </Switch>
                </Suspense>
            </MainErrorBoundary>
        </div>
    </div>
};

const dispatchVendorDetails = (dispatch, productName, productNameKey) => {
    dispatch({
        type: "APP:LOAD-VENDOR_DETAILS",
        payload: {
            vendorizedProductName: productName,
            productNameKey: productNameKey,
        }
    });
};

const App = React.memo(() => {
    const appState = useSelector(state => state.app.appState);
    const { t, ready } = useTranslation(i18nVendorNamespace);
    const dispatch = useDispatch();    

    useEffect(() => {
        if (ready) {
            const productName = t(process.env.REACT_APP_PRODUCT_NAME);
            const productNameKey = process.env.REACT_APP_PRODUCT_NAME;

            dispatchVendorDetails(dispatch, productName, productNameKey);

            setDocumentTitleAndMeta(productName);
        }
    }, [dispatch, t, ready]);

    removeMainLoader();
    return (
        <>
            {appState === 'loading' && <MainLoader/>}
            {appState === 'proxy' && <DialogLazy><ProxyDialog/></DialogLazy>}
            {appState === "setup-wizard" && <DialogLazy><SetupDialog/></DialogLazy>}
            {process.env.REACT_APP_IS_DARK === 'true' && <DialogLazy><Login/></DialogLazy>}
            {!isNcl() && <NgWrapper/>}
             <AppNotifications/>
            <AppRichNotifications />
            {appState === 'ready' && <Switch>
                <Route path="/federated-connection-test" render={() => <FederationTestPage/>}/>
                <Route path="/redirect-to-federated-connection-test" render={() => <RedirectToFederatedConnectionTest/>}/>
                <Route path="/" render={() => <MainApp/>}/>
            </Switch>} 
        </>
    );
});

export default App;
